import React, { Component } from 'react';

import { RelatedEntityGrid as EntityGrid } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelCalendar } from 'hay-core-react';
import { ModelInputNumber } from 'hay-core-react';
import { ModelCurrency } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import { TabViewComErro } from 'hay-core-react';
import { InputNumber } from 'primereact/inputnumber';
import { TabPanel } from 'primereact/tabview';
import ContratoUtilizacao          from '../../../models/ContratoUtilizacao';
import Contrato                    from '../../../models/Contrato';
import Modalidade                  from '../../../models/Modalidade';
import ContratoUtilizacaoSituacaoEnum from '../../../enums/ContratoUtilizacaoSituacaoEnum';

// ─── Esquema de campos ────────────────────────────────────────────────────────

const model = {
    contrato: {
        exibir:       'main.contratoutilizacao.contrato',
        visivel:      true,
        tipo:         'relacao',
        camporelacao: 'razaosocial',
        entidade:     'contrato.entidade',
        template:     'contrato.template',
        validacao:    { obrigatorio: true },
    },
    modalidade: {
        exibir:       'main.contratoutilizacao.modalidade',
        visivel:      true,
        tipo:         'relacao',
        camporelacao: 'nome',
        entidade:     'modalidade.entidade',
        template:     'modalidade.template',
        validacao:    { obrigatorio: true },
    },
    mesreferencia:     { exibir: 'main.contratoutilizacao.mesreferencia',     visivel: true },
    situacao:          { exibir: 'main.contratoutilizacao.situacao',          visivel: true, tipo: 'enum', entidade: 'contratoutilizacao.situacao' },
    datainicio:        { exibir: 'main.contratoutilizacao.datainicio',        visivel: true, tipo: 'date' },
    datatermino:       { exibir: 'main.contratoutilizacao.datatermino',       visivel: true, tipo: 'date' },
    diascompetencia:   { exibir: 'main.contratoutilizacao.diascompetencia',   tipo: 'numeric' },
    diasconsiderados:  { exibir: 'main.contratoutilizacao.diasconsiderados',  tipo: 'numeric' },
    valormodalidade:   { exibir: 'main.contratoutilizacao.valormodalidade',   tipo: 'currency', template: 'valormodalidade.template' },
    valorproporcional: { exibir: 'main.contratoutilizacao.valorproporcional', visivel: true, tipo: 'currency', template: 'valorproporcional.template' },
    valorexcedente:    { exibir: 'main.contratoutilizacao.valorexcedente',    visivel: true, tipo: 'currency', template: 'valorexcedente.template' },
    valortotal:        { exibir: 'main.contratoutilizacao.valortotal',        visivel: true, tipo: 'currency', template: 'valortotal.template' },
    // recursos é um objeto aninhado (sessoes/sms/email/token, cada um com
    // quantidadeatual/limitemodalidade/limitefinal/excedido) - não existe componente Model* pra
    // JSON de objeto (só ModelChips, pra array de string, ver Modalidade.beneficios). Sem
    // `visivel`/`tipo` de propósito (não vira coluna de grid nem campo editável - é gerado só pelo
    // backend), mas PRECISA existir como chave aqui: UtilService.prepararModel só copia
    // valores[k] -> model[k].value pras chaves que já existem no schema `model` (faz
    // lodash.forOwn(model, ...), não itera os dados brutos) - sem essa entrada, o "Ver" sempre
    // exibia o resumo zerado/vazio independente do valor real (bug real encontrado em produção).
    recursos: { exibir: 'main.contratoutilizacao.recursos', template: 'recursos.template' },
};

// ─── Renderers customizados ───────────────────────────────────────────────────

const currencyTemplate = (campo) => (rowData) => UtilService.formatCurrency(rowData[campo]);

const renderers = {
    contrato:   { entidade: Contrato,   template: (rowData) => rowData.contrato?.razaosocial || '' },
    modalidade: { entidade: Modalidade, template: (rowData) => rowData.modalidade?.nome || '' },
    'contratoutilizacao.situacao': ContratoUtilizacaoSituacaoEnum,
    'valormodalidade.template':   currencyTemplate('valormodalidade'),
    'valorproporcional.template': currencyTemplate('valorproporcional'),
    'valorexcedente.template':    currencyTemplate('valorexcedente'),
    'valortotal.template':        currencyTemplate('valortotal'),
    // recursos e um objeto (nao array) - o renderer padrao de coluna sem template faz
    // `children: rowData[col.field]` (ver EntityDataTable.parseBodyTemplate default case), que
    // quebra o React ("Objects are not valid as a React child") se essa coluna for ligada via
    // configurador. Resumo compacto texto-only evita o crash, mesmo sem uso esperado como coluna.
    'recursos.template': (rowData) => RECURSOS_ORDEM
        .map((chave) => `${UtilService.traduzir('main.contratoutilizacao.recursos.' + chave)}: ${rowData.recursos?.[chave]?.quantidadeatual ?? 0}`)
        .join(' · '),
};

// ─── Resumo somente-leitura dos contadores por recurso ────────────────────────

const RECURSOS_ORDEM = ['sessoes', 'sms', 'email', 'token'];

export class ContratoUtilizacaoRecursosResumo extends Component {
    render() {
        const recursos = this.props.recursos || {};
        return (
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="text-left">{UtilService.traduzir('main.contratoutilizacao.recursos')}</th>
                        <th className="text-right">{UtilService.traduzir('main.contratoutilizacao.recursos.quantidadeatual')}</th>
                        <th className="text-right">{UtilService.traduzir('main.contratoutilizacao.recursos.limitemodalidade')}</th>
                        <th className="text-right">{UtilService.traduzir('main.contratoutilizacao.recursos.limitefinal')}</th>
                        <th className="text-right">{UtilService.traduzir('main.contratoutilizacao.recursos.excedido')}</th>
                    </tr>
                </thead>
                <tbody>
                    {RECURSOS_ORDEM.map((chave) => {
                        const r = recursos[chave] || {};
                        return (
                            <tr key={chave}>
                                <td>{UtilService.traduzir('main.contratoutilizacao.recursos.' + chave)}</td>
                                <td className="text-right">{r.quantidadeatual ?? 0}</td>
                                <td className="text-right">{r.limitemodalidade ?? '—'}</td>
                                <td className="text-right">{r.limitefinal ?? '—'}</td>
                                <td className="text-right">{r.excedido ?? 0}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }
}

// ─── Formulário de campos (render prop) ───────────────────────────────────────

class ContratoUtilizacaoFormulario extends Component {
    atualizarRecurso(chave, campo, valor) {
        const { model, contexto } = this.props;
        const recursos = {
            ...(model.recursos.value || {}),
            [chave]: {
                ...(model.recursos.value?.[chave] || {}),
                [campo]: valor,
            },
        };
        contexto.updateModelPath('recursos.value', recursos);
    }

    renderRecursos() {
        const { model } = this.props;
        const campos = ['quantidadeatual', 'limitemodalidade', 'limitefinal', 'excedido'];
        return (
            <div className="grid">
                {RECURSOS_ORDEM.map((chave) => (
                    <div className="col-12 md:col-6 xl:col-3" key={chave}>
                        <div className="border-1 surface-border border-round p-3 h-full">
                            <h4 className="mt-0 mb-3">
                                {UtilService.traduzir('main.contratoutilizacao.recursos.' + chave)}
                            </h4>
                            {campos.map((campo) => {
                                const inputId = `contratoutilizacao-${chave}-${campo}`;
                                return (
                                    <div className="field mb-3" key={inputId}>
                                        <label className="block mb-1 text-sm" htmlFor={inputId}>
                                            {UtilService.traduzir('main.contratoutilizacao.recursos.' + campo)}
                                        </label>
                                        <InputNumber
                                            inputId={inputId}
                                            value={model.recursos.value?.[chave]?.[campo]}
                                            useGrouping={false}
                                            min={0}
                                            className="w-full"
                                            inputClassName="w-full"
                                            onValueChange={(e) => this.atualizarRecurso(chave, campo, e.value)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { model, contexto } = this.props;
        return (
            <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
                <TabPanel header={UtilService.traduzir('main.contratoutilizacao.aba.basica')} leftIcon="pi pi-file mr-2">
                    <div className="grid">
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelDropDown model={model.contrato} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelDropDown model={model.modalidade} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelTextInput model={model.mesreferencia} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-6 mt-3">
                            <ModelDropDown model={model.situacao} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCalendar model={model.datainicio} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCalendar model={model.datatermino} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelInputNumber model={model.diascompetencia} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelInputNumber model={model.diasconsiderados} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCurrency model={model.valormodalidade} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCurrency model={model.valorproporcional} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCurrency model={model.valorexcedente} contexto={contexto} />
                        </div>
                        <div className="field col-12 md:col-3 mt-3">
                            <ModelCurrency model={model.valortotal} contexto={contexto} />
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header={UtilService.traduzir('main.contratoutilizacao.aba.recursos')} leftIcon="pi pi-chart-bar mr-2">
                    {this.renderRecursos()}
                </TabPanel>
            </TabViewComErro>
        );
    }
}

// ─── Template (componente exportado) ─────────────────────────────────────────

class ContratoUtilizacaoTemplate extends Component {
    render() {
        return (
            <EntityGrid
                entidade={ContratoUtilizacao}
                model={model}
                identidade="main.contratoutilizacao"
                permissoes={this.props.permissoes}
                icone="pi-wallet"
                storageKey="dt-contratoutilizacoes"
                relacionados={[]}
                renderers={renderers}
                configuracao={this.props.configuracao}
                ordenacaoPadrao={{ sortField: 'datainicio', sortOrder: -1 }}
                formulario={(m, ctx) => <ContratoUtilizacaoFormulario model={m} contexto={ctx} />}
            />
        );
    }
}

export default ContratoUtilizacaoTemplate;
